import { tv } from "@ui/lib/tv";
import { Popover, type PopoverProps } from "@ui/primitives/Popover";
import type { ReactNode } from "react";

const popoverShell = tv({
  slots: {
    popover: [
      "outline-none",
      "drop-shadow-popover",
      "entering:animate-in entering:fade-in entering:zoom-in-95 entering:duration-150 entering:ease-out",
      "exiting:animate-out exiting:fade-out exiting:zoom-out-95 exiting:duration-100 exiting:ease-in",
    ],
    // Match the trigger pill width when room allows, with a 320px floor so narrow
    // sections (e.g. Who) keep usable popovers. React Aria publishes --trigger-width
    // on the Popover element.
    //
    // React Aria caps the popover's height to the space below the trigger, but as an
    // inline style on the Popover, not the card — max-h-[inherit] passes it down so a
    // list that outgrows a short viewport scrolls inside the card instead of running
    // off-screen. Panels down to the scroller need min-h-0 to actually yield.
    card: "flex max-h-[inherit] w-[max(320px,var(--trigger-width))] flex-col overflow-hidden rounded-3xl bg-white outline-none",
  },
});

export interface SearchBarPopoverShellProps extends Omit<PopoverProps, "children"> {
  children: ReactNode;
  ariaLabel: string;
}

export function SearchBarPopoverShell({ children, ariaLabel, ...props }: SearchBarPopoverShellProps) {
  const styles = popoverShell();
  // crossOffset=-12 aligns the popover's left edge with the hover pill's left
  // edge. The pill extends -inset-x-3 (12px) past each section button, so a
  // uniform -12px shift makes the popover read as a continuation of the pill
  // for every section (first, middle, last).
  return (
    <Popover className={styles.popover()} crossOffset={-12} offset={8} placement="bottom start" {...props}>
      <section aria-label={ariaLabel} className={styles.card()}>
        {children}
      </section>
    </Popover>
  );
}
